import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Ban,
  Check,
  Copy,
  Inbox,
  KeyRound,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  Trash2,
  UserCheck,
  UsersRound,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import { EmptyState, Modal, PageHeader, StatCard, StatusBadge, Surface } from '../../components/ui';

function UserStatus({ user, t }) {
  if (user.is_banned) return <StatusBadge tone="danger">{t('adminUsers.statusBanned')}</StatusBadge>;
  if (user.is_admin) return <StatusBadge tone="accent">{t('adminUsers.statusAdmin')}</StatusBadge>;
  return <StatusBadge tone="success">{t('adminUsers.statusActive')}</StatusBadge>;
}

function GeneratedPasswordModal({ username, password, onClose, t }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal title={t('adminUsers.resetPwdTitle')} onClose={onClose} labelledBy="generated-password-title">
      <p className="text-muted small">{t('adminUsers.resetPwdUser')} <strong className="text-strong">{username}</strong></p>
      <div className="secret-panel" role="status">
        <span className="secret-label">{t('adminUsers.generatedPwd')}</span>
        <div className="secret-value-row">
          <code className="secret-value">{password}</code>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleCopy}>
            {copied ? <Check className="icon" aria-hidden="true" /> : <Copy className="icon" aria-hidden="true" />}
            {copied ? t('deviceDetail.copied') : t('adminUsers.copyPwd')}
          </button>
        </div>
      </div>
      <p className="form-text mt-3">{t('adminUsers.generatedPwdHint')}</p>
      <div className="modal-actions"><button type="button" className="btn btn-primary" onClick={onClose}>{t('adminUsers.resetPwdDone')}</button></div>
    </Modal>
  );
}

export default function UsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setUsers(await api.getUsers());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markBusy = (id, value) => setBusy((state) => ({ ...state, [id]: value }));

  const patchUser = async (user, data, confirmation) => {
    if (confirmation && !window.confirm(confirmation)) return;
    markBusy(user.id, true);
    setError('');
    try {
      await api.updateUser(user.id, data);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      markBusy(user.id, false);
    }
  };

  const resetPassword = async (user) => {
    if (!window.confirm(t('adminUsers.confirmResetPwd', { username: user.username }))) return;
    markBusy(user.id, true);
    setError('');
    try {
      const result = await api.resetUserPassword(user.id);
      setGeneratedPassword({ username: user.username, password: result.new_password });
    } catch (err) {
      setError(err.message);
    } finally {
      markBusy(user.id, false);
    }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(t('adminUsers.confirmDelete', { username: user.username }))) return;
    markBusy(user.id, true);
    setError('');
    try {
      await api.deleteUser(user.id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      markBusy(user.id, false);
    }
  };

  const total = users.length;
  const banned = users.filter((user) => user.is_banned).length;
  const admins = users.filter((user) => user.is_admin).length;

  return (
    <div className="page">
      <PageHeader
        icon={UsersRound}
        title={t('adminUsers.title')}
        subtitle={t('adminUsers.subtitle')}
        actions={<button type="button" className="btn btn-outline-secondary" onClick={load}><RefreshCw className="icon" aria-hidden="true" />{t('adminDevices.refresh')}</button>}
      />
      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      {!loading && (
        <div className="stat-grid">
          <StatCard icon={UsersRound} label={t('adminUsers.statsTotal')} value={total} />
          <StatCard icon={UserCheck} label={t('adminUsers.statsActive')} value={total - banned} tone="success" />
          <StatCard icon={Ban} label={t('adminUsers.statsBanned')} value={banned} tone="danger" />
          <StatCard icon={ShieldCheck} label={t('adminUsers.statsAdmins')} value={admins} tone="warning" />
        </div>
      )}

      <Surface
        title={total > 0 ? t('adminUsers.countUsers', { count: total }) : t('adminUsers.noUsers')}
        icon={UsersRound}
        action={<button type="button" className="icon-btn" onClick={load} title={t('adminDevices.refresh')} aria-label={t('adminDevices.refresh')}><RefreshCw className="icon" aria-hidden="true" /></button>}
      >
        {loading ? <div className="empty-state"><div className="spinner-border spinner-border-sm" role="status" /></div> : users.length === 0 ? (
          <EmptyState icon={Inbox} title={t('adminUsers.noUsers')} />
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead><tr><th>{t('adminUsers.colUsername')}</th><th>{t('adminUsers.colEmail')}</th><th>{t('adminUsers.colStatus')}</th><th>{t('adminUsers.colDevices')}</th><th>{t('adminUsers.colRegistered')}</th><th>{t('adminUsers.colActions')}</th></tr></thead>
              <tbody>{users.map((user) => {
                const isBusy = Boolean(busy[user.id]);
                return (
                  <tr
                    key={user.id}
                    className={isBusy ? 'row-busy' : ''}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(`/admin/users/${user.id}`)}
                    onKeyDown={(event) => event.key === 'Enter' && navigate(`/admin/users/${user.id}`)}
                  >
                    <td><span className={`user-avatar tone-${user.is_banned ? 'danger' : user.is_admin ? 'accent' : 'success'}`}>{user.username.slice(0, 1).toUpperCase()}</span><strong className="ms-2 text-strong">{user.username}</strong></td>
                    <td className="small text-muted">{user.email}</td>
                    <td><UserStatus user={user} t={t} /></td>
                    <td className="text-center">{user.device_count}</td>
                    <td className="small text-muted text-nowrap">{new Date(user.created_at).toLocaleDateString()}</td>
                    <td onClick={(event) => event.stopPropagation()}>
                      <div className="action-group">
                        <button
                          type="button"
                          className={`btn btn-sm ${user.is_banned ? 'btn-outline-success' : 'btn-outline-danger'}`}
                          disabled={isBusy}
                          onClick={() => patchUser(user, { is_banned: !user.is_banned }, t(user.is_banned ? 'adminUsers.confirmUnban' : 'adminUsers.confirmBan', { username: user.username }))}
                        >
                          {user.is_banned ? <ShieldCheck className="icon" aria-hidden="true" /> : <Ban className="icon" aria-hidden="true" />}
                          {t(user.is_banned ? 'adminUsers.actionUnban' : 'adminUsers.actionBan')}
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${user.is_admin ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                          disabled={isBusy}
                          onClick={() => patchUser(user, { is_admin: !user.is_admin }, t(user.is_admin ? 'adminUsers.confirmRevokeAdmin' : 'adminUsers.confirmMakeAdmin', { username: user.username }))}
                        >
                          {user.is_admin ? <ShieldOff className="icon" aria-hidden="true" /> : <ShieldCheck className="icon" aria-hidden="true" />}
                          {t(user.is_admin ? 'adminUsers.actionRevokeAdmin' : 'adminUsers.actionMakeAdmin')}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-warning" disabled={isBusy} onClick={() => resetPassword(user)}><KeyRound className="icon" aria-hidden="true" />{t('adminUsers.actionResetPwd')}</button>
                        <button type="button" className="icon-btn danger" disabled={isBusy} onClick={() => deleteUser(user)} title={t('adminDevices.delete')} aria-label={t('adminDevices.delete')}><Trash2 className="icon" aria-hidden="true" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        )}
      </Surface>

      {generatedPassword && <GeneratedPasswordModal {...generatedPassword} t={t} onClose={() => setGeneratedPassword(null)} />}
    </div>
  );
}
